import { normalizeTemplates } from '../../../../../shared/pkg/prepacks/templateVariants.js'
import { deriveBetType, legBetLabel, legNeedsEvent, packEventLabel, packGames, packTypeLabel } from '../../../../../shared/pkg/prepacks/templateRendering.js'
import { BANNER_FILL_CARD, BANNER_FILL_VARIABLE, BANNER_FILLS, BANNER_HIDDEN_VAR, BANNER_IMAGE, BANNER_IMAGE_VARIABLE, BANNER_MODE_DYNAMIC, BANNER_MODE_STATIC, TITLE_ALIGNMENT_CENTER, TITLE_PLACEMENT_OVER, bannerImageValue, bannerOverlay, bannerPictureAspect, bannerTitleOf, cardScrim, centerWash, effectiveBannerMode, isBannerImageUrl, normalizeBannerImage, scheduleBannerFill, scheduleBannerFramingVars } from '../../../../../shared/pkg/prepacks/templateBuilder.js'
import { MAX_PLAYER_NUMBER_DIGITS, normalizePlayerNumber, svgDataUri } from '../../../../../shared/pkg/prepacks/jerseySvg.js'
import { JERSEY_NUMBERS_RETIRED } from '../../../../../shared/pkg/prepacks/templateBuilder.js'
import { prepackSportLabel } from '@/utils/kambiSports'
import { packSportId } from './prepackRecipeModel'

const NON_DIGITS_PATTERN = /\D/g
const LEADING_NUMBER_ZEROES_PATTERN = /^0+(?=[1-9])/
const WHITESPACE_PATTERN = /\s+/
const NON_LETTERS_PATTERN = /[^a-z]/gi
const TEAM_CODE_PATTERN = /^[a-z0-9]{2,4}$/
const JERSEY_TOKEN_PATTERN = /\{\{LEG_\d+_JERSEY(?:_PLAIN)?\}\}/
const STYLED_JERSEY_TOKEN_PATTERN = /\{\{LEG_\d+_JERSEY\}\}/

export { BET_TYPE_LABELS, betTypeLabel, deriveBetType, legBetLabel, legNeedsEvent, packTypeLabel } from '../../../../../shared/pkg/prepacks/templateRendering.js'

export const STARTER_TEMPLATES = normalizeTemplates().filter((template) => !template.isFallback)

// Sample teams in the shape the server stores on a leg (#572). None has a
// logo: the builder asks the server for the template's team (the Eagles by
// default) as the organization would get it (withPreviewTeam). No sample
// carries a shirt number: no provider supplies one, so only the builder adds
// sample numbers, while the template shows them.
/** The catalog id of the preview team the server sends (previewTeamCatalogID in the Studio API). */
export const PREVIEW_TEAM_ID = 'nfl:phi'
const EAGLES = { id: PREVIEW_TEAM_ID, name: 'Philadelphia Eagles', abbr: 'PHI', colors: ['#004c54', '#a5acaf'], kit: { pattern: 'sleeve-stripes', body: '#004c54', trim: '#a5acaf', number_color: '#ffffff' } }
const THUNDER = { name: 'Oklahoma City Thunder', abbr: 'OKC', colors: ['#007ac1', '#ef3b24'], kit: { pattern: 'solid', body: '#007ac1', trim: '#ef3b24' } }
const YANKEES = { name: 'New York Yankees', abbr: 'NYY', colors: ['#0c2340', '#c4ced3'], kit: { pattern: 'pinstripes', body: '#ffffff', trim: '#0c2340', number_color: '#0c2340' } }
const RED_SOX = { name: 'Boston Red Sox', abbr: 'BOS', colors: ['#bd3039', '#0c2340'], kit: { pattern: 'solid', body: '#ffffff', trim: '#bd3039', number_color: '#bd3039' } }
/** The sample teams with a kit, for the builder's sample games (#813). */
export const SAMPLE_TEAM_KITS = Object.freeze({ EAGLES, THUNDER, YANKEES, RED_SOX })

/** The pack every template preview is drawn with. */
export const TEMPLATE_SAMPLE_PACK = {
  id: 'template-preview',
  title: 'Sunday Night SGP',
  type: 'SGP',
  // Its legs carry no ids to derive a bet type from, so it names the one a real pack of these legs derives: the
  // badge then reads as a run's does (#1031).
  bet_type: 'same_game_parlay',
  combinedOdds: '+650',
  copies: 1248,
  summary: 'Three Eagles to hit their Sunday night marks',
  sport: 'american_football',
  legs: [
    { selection_name: 'Jalen Hurts', market_label: 'Over 249.5 Pass Yds', odds: '-115', sport: 'american_football', event_label: 'PHI @ DAL', team: EAGLES },
    { selection_name: 'A.J. Brown', market_label: 'Anytime TD', odds: '+140', sport: 'american_football', event_label: 'PHI @ DAL', team: EAGLES },
    { selection_name: 'Philadelphia Eagles', market_label: 'Moneyline', odds: '-180', sport: 'american_football', event_label: 'PHI @ DAL', team: EAGLES },
  ],
}

/** A few different packs, for seeing a template the way a feed shows it: side by side, with short and long cards. */
export const TEMPLATE_SAMPLE_FEED = [
  TEMPLATE_SAMPLE_PACK,
  {
    id: 'template-preview-2',
    title: 'Thunder Double',
    type: 'Parlay',
    bet_type: 'parlay',
    combinedOdds: '+264',
    copies: 312,
    sport: 'basketball',
    legs: [
      { selection_name: 'Shai Gilgeous-Alexander', market_label: 'Over 30.5 Points', odds: '-110', sport: 'basketball', event_label: 'OKC @ MIN', team: THUNDER },
      { selection_name: 'Oklahoma City Thunder', market_label: 'Moneyline', odds: '-150', sport: 'basketball', event_label: 'OKC @ MIN', team: THUNDER },
    ],
  },
  {
    id: 'template-preview-3',
    title: 'Friday Night Long Shot',
    type: 'Parlay',
    bet_type: 'parlay',
    combinedOdds: '+2150',
    copies: 0,
    sport: 'baseball',
    legs: [
      { selection_name: 'Aaron Judge', market_label: 'To Hit a Home Run', odds: '+240', sport: 'baseball', event_label: 'NYY @ BOS', team: YANKEES },
      { selection_name: 'Rafael Devers', market_label: 'Over 1.5 Total Bases', odds: '+120', sport: 'baseball', event_label: 'NYY @ BOS', team: RED_SOX },
      { selection_name: 'New York Yankees', market_label: 'Run Line -1.5', odds: '+135', sport: 'baseball', event_label: 'NYY @ BOS', team: YANKEES },
      { selection_name: 'Gerrit Cole', market_label: 'Over 6.5 Strikeouts', odds: '-125', sport: 'baseball', event_label: 'NYY @ BOS', team: YANKEES },
      { selection_name: 'Game Total', market_label: 'Under 8.5 Runs', odds: '-105', sport: 'baseball', event_label: 'NYY @ BOS' },
    ],
  },
]

// The schedule builder's sample pack per builder sport (issue 763), keyed by the
// builder's sport ids (`football` is American football there, `soccer` soccer).
// `sport` is what the server stores on a leg (prepacks.SportKey: soccer is
// Kambi's `football`), `league` the league id the sample's games belong to
// (the card's glyph reads it), and each game label follows how the book writes
// that sport's games: "Away @ Home" in US leagues, "Home - Away" elsewhere.
// Six legs, so the builder can show every leg count, except golf and
// motorsports, which offer Straight packs only (#688): one leg each, and their
// `type` is Straight whatever pack type the schedule has. Only the sample
// teams above carry a team. Illustrative, like every sample.
const sportLegs = (sport, eventLabel, legs) => legs.map(([selection, market, odds, team]) => ({
  selection_name: selection,
  market_label: market,
  odds,
  sport,
  event_label: eventLabel,
  ...(team ? { team } : {}),
}))

export const SAMPLE_PACKS_BY_SPORT = Object.freeze({
  football: {
    sport: 'american_football',
    league: 'NFL',
    title: TEMPLATE_SAMPLE_PACK.title,
    summary: TEMPLATE_SAMPLE_PACK.summary,
    legs: [
      ...TEMPLATE_SAMPLE_PACK.legs,
      ...sportLegs('american_football', 'PHI @ DAL', [
        ['DeVonta Smith', 'Over 4.5 Receptions', '+105'],
        ['CeeDee Lamb', 'Over 74.5 Rec Yds', '-120'],
        ['Game Total', 'Over 47.5 Points', '-110'],
      ]),
    ],
  },
  basketball: {
    sport: 'basketball',
    league: 'NBA',
    title: 'Thunder Road Trip',
    summary: 'The Thunder stars to deliver in Minnesota',
    legs: sportLegs('basketball', 'OKC @ MIN', [
      ['Shai Gilgeous-Alexander', 'Over 30.5 Points', '-110', THUNDER],
      ['Chet Holmgren', 'Over 8.5 Rebounds', '-115', THUNDER],
      ['Oklahoma City Thunder', 'Moneyline', '-150', THUNDER],
      ['Anthony Edwards', 'Over 4.5 Assists', '+105'],
      ['Oklahoma City Thunder -4.5', 'Spread', '-110', THUNDER],
      ['Game Total', 'Over 224.5 Points', '-105'],
    ]),
  },
  baseball: {
    sport: 'baseball',
    league: 'MLB',
    title: 'Bronx Bombers at Fenway',
    summary: 'The Yankees to power through Boston',
    legs: sportLegs('baseball', 'NYY @ BOS', [
      ['Aaron Judge', 'To Hit a Home Run', '+240', YANKEES],
      ['Gerrit Cole', 'Over 6.5 Strikeouts', '-125', YANKEES],
      ['New York Yankees', 'Moneyline', '-135', YANKEES],
      ['Jarren Duran', 'Over 1.5 Total Bases', '+120', RED_SOX],
      ['New York Yankees -1.5', 'Run Line', '+135', YANKEES],
      ['Game Total', 'Under 8.5 Runs', '-105'],
    ]),
  },
  hockey: {
    sport: 'hockey',
    league: 'NHL',
    title: 'Original Six Saturday',
    summary: 'The Leafs to light the lamp in Boston',
    legs: sportLegs('hockey', 'TOR @ BOS', [
      ['Auston Matthews', 'Anytime Goal', '+130'],
      ['William Nylander', 'Over 2.5 Shots on Goal', '-125'],
      ['Toronto Maple Leafs', 'Moneyline', '+105'],
      ['David Pastrnak', 'Over 0.5 Points', '-165'],
      ['Boston Bruins +1.5', 'Puck Line', '-230'],
      ['Game Total', 'Over 6.5 Goals', '+110'],
    ]),
  },
  soccer: {
    sport: 'football',
    league: 'EPL',
    title: 'London Derby Day',
    summary: 'Arsenal to take the derby',
    legs: sportLegs('football', 'ARS - CHE', [
      ['Bukayo Saka', 'Anytime Goalscorer', '+180'],
      ['Arsenal', 'Match Result', '-110'],
      ['Both Teams To Score', 'Yes', '-120'],
      ['Cole Palmer', 'Over 1.5 Shots on Target', '+150'],
      ['Game Total', 'Over 2.5 Goals', '-105'],
      ['Arsenal', 'Over 5.5 Corners', '-115'],
    ]),
  },
  rugby: {
    sport: 'rugby',
    league: 'SIX_NATIONS',
    title: 'Le Crunch Specials',
    summary: 'England to edge France at Twickenham',
    legs: sportLegs('rugby', 'England - France', [
      ['Tommy Freeman', 'Anytime Try Scorer', '+150'],
      ['England', 'Match Result', '-140'],
      ['England 1-12', 'Winning Margin', '+240'],
      ['Louis Bielle-Biarrey', 'Anytime Try Scorer', '+120'],
      ['Game Total', 'Over 48.5 Points', '-110'],
      ['Marcus Smith', 'Over 8.5 Points', '-115'],
    ]),
  },
  motorsports: {
    sport: 'motorsports',
    league: 'INDYCAR',
    type: 'Straight',
    title: 'Indy 500 Pick',
    summary: 'Palou to win at the Brickyard',
    legs: sportLegs('motorsports', 'Indianapolis 500', [
      ['Alex Palou', 'Race Winner', '+350'],
    ]),
  },
  mma: {
    sport: 'mma',
    league: 'UFC',
    title: 'Title Fight Special',
    summary: 'Pantoja to defend his belt',
    legs: sportLegs('mma', 'Pantoja - Asakura', [
      ['Alexandre Pantoja', 'Moneyline', '-250'],
      ['Pantoja by Submission', 'Method of Victory', '+200'],
      ['Fight Total', 'Over 2.5 Rounds', '-130'],
      ['Fight Goes the Distance', 'No', '-180'],
      ['Alexandre Pantoja', 'Over 1.5 Takedowns', '+110'],
      ['Kai Asakura', 'Over 25.5 Significant Strikes', '-115'],
    ]),
  },
  golf: {
    sport: 'golf',
    league: 'PGA',
    type: 'Straight',
    title: 'Masters Sunday Pick',
    summary: 'Scheffler to win another green jacket',
    legs: sportLegs('golf', 'The Masters', [
      ['Scottie Scheffler', 'To Win', '+450'],
    ]),
  },
  tennis: {
    sport: 'tennis',
    league: 'ATP',
    title: 'Alcaraz v Sinner Special',
    summary: 'Alcaraz to win the rivalry match',
    legs: sportLegs('tennis', 'Alcaraz - Sinner', [
      ['Carlos Alcaraz', 'Match Winner', '-120'],
      ['Match Total', 'Over 22.5 Games', '-115'],
      ['Alcaraz 2-1', 'Set Betting', '+300'],
      ['Jannik Sinner', 'Over 8.5 Aces', '-110'],
      ['Carlos Alcaraz -2.5', 'Game Handicap', '+105'],
      ['Tiebreak in Match', 'Yes', '+100'],
    ]),
  },
  cricket: {
    sport: 'cricket',
    league: 'SA20',
    title: 'Royals v MI Cape Town Special',
    summary: 'The Royals to win at Boland Park',
    legs: sportLegs('cricket', 'Paarl Royals - MI Cape Town', [
      ['Paarl Royals', 'Match Odds', '-130'],
      ['David Miller', 'Over 24.5 Runs', '-115'],
      ['Ryan Rickelton', 'To Score a Half-Century', '+260'],
      ['Paarl Royals', 'Most Sixes', '-110'],
      ['Kagiso Rabada', 'Top Wicket Taker', '+350'],
      ['Rashid Khan', 'Over 1.5 Wickets', '+120'],
    ]),
  },
})

/** The builder sport whose sample stands in when the schedule names none: today's football sample. */
export const DEFAULT_SAMPLE_SPORT = 'football'

/** The sample pack for a builder sport id; football's for an unknown one. */
export const samplePackForSport = (sport) => SAMPLE_PACKS_BY_SPORT[sport] || SAMPLE_PACKS_BY_SPORT[DEFAULT_SAMPLE_SPORT]

// A stand-in for a team logo in the builder preview while the organization
// has none to show (logos off for it, the icon service without one, or the
// request still running or failed), so an operator who switches logos on still
// sees where one goes. A dashed outline with a picture inside, so nobody
// mistakes it for a team's crest.
const PREVIEW_LOGO = svgDataUri("<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'><path d='M20 3L34 8V19Q34 31 20 37Q6 31 6 19V8Z' fill='none' stroke='#8a94a0' stroke-width='2' stroke-dasharray='3.5 2.5' stroke-linejoin='round'/><circle cx='15.5' cy='15' r='2.4' fill='#8a94a0'/><path d='M11 27L17 20L21 24L24 21L29 27Z' fill='#8a94a0'/></svg>")
export const PREVIEW_LOGO_LEG = 3
// The same leg as the legs array indexes it.
const TEAM_BET_INDEX = PREVIEW_LOGO_LEG - 1

/**
 * The builder preview's template, with the placeholder logo on the sample's
 * team bets when the template shows logos: the third leg, and the fourth (the
 * spread) when the sample shows it (sampleTeamBetLegs).
 */
export function withPreviewLogo(template, legs = [PREVIEW_LOGO_LEG]) {
  let htmlCode = template?.htmlCode
  for (const leg of legs) htmlCode = htmlCode?.replace(`src="{{LEG_${leg}_LOGO}}"`, `src="${PREVIEW_LOGO}"`)
  return htmlCode === template?.htmlCode ? template : { ...template, htmlCode }
}

/** The legs (1-based) of a sample of `count` legs that bet on the sample's team, and so carry its logo. */
export function sampleTeamBetLegs(count) {
  return [PREVIEW_LOGO_LEG, PREVIEW_LOGO_LEG + 1].filter((leg) => leg <= count)
}

// The sample's first two legs are player props and the third is the team bet
// (PREVIEW_LOGO_LEG). The builder gives the player legs these shirt numbers
// while the template shows numbers, so the Jerseys "Numbers" option has a
// visible effect; TEMPLATE_SAMPLE_PACK itself stays without numbers.
/**
 * A shirt number field's value after a keystroke (the composer's Jerseys box and the builder's sample): the shared
 * `normalizePlayerNumber` rule, with any character other than a digit ignored first so a stray key never empties it,
 * and the digits capped (not refused) so a third digit or a pasted "#12" keeps the number. The fields carry no
 * maxLength: the browser applies that before this runs, and "#12" would reach it as "#1".
 */
export const typedPlayerNumber = (value) => normalizePlayerNumber(String(value ?? '').replace(NON_DIGITS_PATTERN, '').replace(LEADING_NUMBER_ZEROES_PATTERN, '').slice(0, MAX_PLAYER_NUMBER_DIGITS))

/** The sample player legs' shirt numbers unless the operator types others (Sample content, #602). */
export const SAMPLE_PLAYER_NUMBERS = ['1', '11']
// Sample copy for a team other than the Eagles (#602), by sport as legs carry it
// (the catalog's `sport`; "football" is soccer, as Kambi names it, #611): the two
// player legs as "<Nickname> <role>" with a market that sport has.
const SAMPLE_PLAYER_LEGS = {
  american_football: [['QB', 'Over 249.5 Pass Yds'], ['WR', 'Anytime TD']],
  basketball: [['guard', 'Over 30.5 Points'], ['center', 'Over 10.5 Rebounds']],
  baseball: [['slugger', 'To Hit a Home Run'], ['starter', 'Over 6.5 Strikeouts']],
  hockey: [['winger', 'Anytime Goal'], ['goalie', 'Over 28.5 Saves']],
  football: [['striker', 'Anytime Goalscorer'], ['winger', 'Over 1.5 Shots on Target']],
  rugby: [['wing', 'Anytime Try Scorer'], ['fly-half', 'Over 8.5 Points']],
}
// The sample game's opponent per league, and the one used when the team is that opponent.
const LEAGUE_OPPONENTS = { NFL: ['DAL', 'PHI'], NBA: ['MIN', 'OKC'], MLB: ['BOS', 'NYY'], NHL: ['BOS', 'TOR'], WNBA: ['LV', 'NY'], NCAAF: ['OSU', 'MICH'], MLS: ['MIA', 'LAFC'], EPL: ['CHE', 'ARS'], LALIGA: ['RMA', 'BAR'], RUGBY: ['NZL', 'RSA'] }
// Soccer (#611): Kambi writes its games home first ("ARS - CHE"), and a club's short name is no plural, so the
// sample counts its players ("Three Spurs players") where a US nickname counts itself ("Three Eagles"). Rugby
// games are home first too ("RSA - NZL"); "Springboks" counts itself.
const SOCCER = 'football'
const HOME_FIRST_SPORTS = new Set([SOCCER, 'rugby'])

/** What the sample calls a team's players: the catalog nickname the server sends, else the name's last word ("Cowboys"). */
export function teamNickname(team) {
  const nickname = typeof team?.nickname === 'string' ? team.nickname.trim() : ''
  return nickname || String(team?.name || '').trim().split(WHITESPACE_PATTERN).at(-1) || ''
}

/**
 * A short code for a team the catalog has none for, so the game label stays one line: the school's
 * initials ("Air Force Falcons" → AF) or the first three letters of a one-word school ("Hawai'i" → HAW).
 * A long nickname in the label ("Rainbow Warriors @ OSU") wrapped every leg of the card.
 */
function schoolCode(name, nickname) {
  const full = String(name || '').trim()
  const school = (nickname && full.endsWith(nickname) ? full.slice(0, -nickname.length) : full).trim()
  const words = school.split(WHITESPACE_PATTERN).map((word) => word.replace(NON_LETTERS_PATTERN, '')).filter(Boolean)
  if (words.length >= 2) return words.slice(0, 4).map((word) => word[0]).join('').toUpperCase()
  return (words[0] || '').slice(0, 3).toUpperCase()
}

/**
 * The sample pack's legs and copy rewritten for another catalog team, in its own sport and game. `sport` is the
 * catalog's (GET .../prepacks/templates/teams), as legs carry it; without one, or for a sport the sample has no
 * copy for, the sample keeps its own.
 */
// The sport each catalog league's legs carry, for a team whose catalog entry is not in yet (the list still loading,
// or failed): the id names the league, and the Eagles' sport would be wrong for a Lakers template.
const LEAGUE_SPORTS = { NFL: 'american_football', NCAAF: 'american_football', NBA: 'basketball', WNBA: 'basketball', MLB: 'baseball', NHL: 'hockey', MLS: 'football', EPL: 'football', LALIGA: 'football', RUGBY: 'rugby' }

function sampleForTeam(pack, team, nickname, sport) {
  // The id names the league ("nfl:dal"), which picks the sample game; a leg's team carries no league of its own.
  const [leaguePart, teamPart = ''] = String(team.id).split(':')
  const league = leaguePart.toUpperCase()
  if (!SAMPLE_PLAYER_LEGS[sport]) sport = LEAGUE_SPORTS[league] || pack.sport
  // Most NCAAF ids end in the full name ("ncaaf:air-force-falcons") and the catalog has no code for them, so
  // the id only stands in for a code when it reads as one; otherwise the game reads "Falcons @ OSU".
  const abbr = typeof team.abbr === 'string' && team.abbr ? team.abbr
    : TEAM_CODE_PATTERN.test(teamPart) ? teamPart.toUpperCase() : schoolCode(team.name, nickname) || teamPart.toUpperCase()
  const [opponent, otherwise] = LEAGUE_OPPONENTS[league] || LEAGUE_OPPONENTS.NFL
  const event = `${abbr} ${HOME_FIRST_SPORTS.has(sport) ? '-' : '@'} ${abbr === opponent ? otherwise : opponent}`
  const players = SAMPLE_PLAYER_LEGS[sport]
  return {
    ...pack,
    sport,
    summary: sampleSummary(nickname, pack.legs.length, false, sport),
    legs: pack.legs.map((leg, index) => {
      const copy = index < players.length
        ? { selection_name: `${nickname} ${players[index][0]}`, market_label: players[index][1] }
        : index === TEAM_BET_INDEX ? { selection_name: team.name, market_label: 'Moneyline' } : {}
      return { ...leg, ...copy, sport, event_label: event }
    }),
  }
}

// The sample's summary in words that hold at its length: two legs are both the team's player props, while four
// or five add the spread and the total, so the team is said to cover instead of counting bets. Only the
// Eagles' sample is the Sunday night game; another team's sample keeps no day.
const SAMPLE_COUNT_WORDS = { 2: 'Two', 3: 'Three' }
function sampleSummary(nickname, count, sundayNight, sport) {
  if (!SAMPLE_COUNT_WORDS[count]) return `${nickname} to cover${sundayNight ? ' on Sunday night' : ''}`
  const players = sport === SOCCER ? `${nickname} players` : nickname
  return `${SAMPLE_COUNT_WORDS[count]} ${players} to hit their${sundayNight ? ' Sunday night' : ''} marks`
}

// An answer without an id is the default team, as servers before #602 sent it, and so is no answer yet.
const isSampleEagles = (team) => !team || typeof team !== 'object' || !team.id || team.id === PREVIEW_TEAM_ID

/**
 * The sample pack with the preview team (GET .../prepacks/templates/preview-team,
 * the template's team when it has one, #602) on every leg that carries a team:
 * its catalog colors and kit, and its logo_url, when the organization may show
 * one, on the team bet only, as the server stores it (a player prop gets no
 * logo). The Eagles keep the sample as it is (Hurts, Brown, the Eagles
 * moneyline); any other team gets copy for its sport and game. `numbers` puts
 * sample shirt numbers on the two player legs. Preview data only; nothing is saved.
 */
export function withPreviewTeam(pack, team, { numbers = false } = {}) {
  const dressed = team && typeof team === 'object' ? dressSample(pack, team) : pack
  // `true` is the sample's own numbers; a list is the operator's, where an empty one leaves that jersey plain.
  const shirts = numbers === true ? SAMPLE_PLAYER_NUMBERS : Array.isArray(numbers) ? numbers.slice(0, SAMPLE_PLAYER_NUMBERS.length) : []
  if (!shirts.length) return dressed
  return {
    ...dressed,
    legs: dressed.legs.map((leg, index) => {
      const number = normalizePlayerNumber(shirts[index])
      return index < shirts.length && leg.team && number ? { ...leg, team: { ...leg.team, number } } : leg
    }),
  }
}

/** The leg counts the builder's sample offers (#602); the sample pack itself has three. */
export const SAMPLE_LEG_COUNTS = [2, 3, 4, 5]
/** The most bets placed the sample may claim. */
export const MAX_SAMPLE_COPIES = 99999
/** The longest sample summary: what a card has room for. */
export const MAX_SAMPLE_SUMMARY = 140
/**
 * What the builder's Sample content starts from, and what "Reset sample" brings back (#602). An empty title or
 * summary shows the sample's own (the summary follows the preview team); an empty team follows the template's.
 */
export const SAMPLE_DEFAULTS = { title: '', summary: '', team: '', legs: TEMPLATE_SAMPLE_PACK.legs.length, copies: String(TEMPLATE_SAMPLE_PACK.copies), numbers: SAMPLE_PLAYER_NUMBERS }
/** Whether the sample is still the default. A title or summary of spaces shows the sample's own, so it is no edit. */
export const sampleIsDefault = (sample) => Object.keys(SAMPLE_DEFAULTS).every((key) => {
  if (key === 'numbers') return sample.numbers.every((number, index) => number === SAMPLE_DEFAULTS.numbers[index])
  if (key === 'title' || key === 'summary') return sample[key].trim() === SAMPLE_DEFAULTS[key]
  return sample[key] === SAMPLE_DEFAULTS[key]
})

/**
 * The sample as the template saves it (`builder.sample`, #997): only what differs from the defaults, so an untouched
 * sample saves nothing. Bets placed save as a number; an empty field keeps the sample's own count.
 */
export function savedSample(sample) {
  const saved = {}
  const title = sample.title.trim()
  const summary = sample.summary.trim()
  if (title) saved.title = title
  if (summary) saved.summary = summary
  if (sample.team) saved.team = sample.team
  if (sample.legs !== SAMPLE_DEFAULTS.legs) saved.legs = sample.legs
  if (sample.copies !== '' && sample.copies !== SAMPLE_DEFAULTS.copies) saved.copies = Number(sample.copies)
  if (sample.numbers.some((number, index) => number !== SAMPLE_DEFAULTS.numbers[index])) saved.numbers = [...sample.numbers]
  return Object.keys(saved).length ? saved : undefined
}

/** The builder's Sample content for a saved template's `builder.sample` (#997): the defaults where it saved nothing usable. */
export function sampleFromSaved(saved) {
  if (!saved || typeof saved !== 'object') return SAMPLE_DEFAULTS
  const text = (value) => (typeof value === 'string' ? value : '')
  return {
    title: text(saved.title),
    summary: text(saved.summary),
    team: text(saved.team),
    legs: SAMPLE_LEG_COUNTS.includes(saved.legs) ? saved.legs : SAMPLE_DEFAULTS.legs,
    copies: Number.isInteger(saved.copies) && saved.copies >= 0 && saved.copies <= MAX_SAMPLE_COPIES ? String(saved.copies) : SAMPLE_DEFAULTS.copies,
    numbers: Array.isArray(saved.numbers) ? SAMPLE_PLAYER_NUMBERS.map((_number, index) => normalizePlayerNumber(saved.numbers[index])) : SAMPLE_DEFAULTS.numbers,
  }
}

/** The catalog team a saved template previews with (#1121): its sample's (#997), else its own (#602), else '' (the Eagles). */
export function templateSampleTeamId(template) {
  const builder = template?.builder
  const text = (value) => (typeof value === 'string' ? value.trim() : '')
  return text(builder?.sample?.team) || text(builder?.team)
}

/**
 * The preview team with the catalog's word on it (nickname and sport), which the server's dressed answer lacks;
 * the catalog entry alone while the dressed team is not in yet. Undefined is the Eagles sample.
 */
export function sampleTeamWithCatalog(previewTeam, catalogEntry) {
  const team = previewTeam || catalogEntry
  if (!team) return undefined
  return catalogEntry ? { ...team, nickname: catalogEntry.nickname, sport: catalogEntry.sport } : team
}

/**
 * A saved template's card as the Templates gallery shows it (#1121): the builder's sample as the template saved
 * it (#997) on `team` (sampleTeamWithCatalog), shirt numbers only while the compiled markup draws them. A
 * template without a builder (the code editor) keeps the default sample, as its editor shows it.
 */
export function templateSamplePack(template, team) {
  if (!template?.builder) return TEMPLATE_SAMPLE_PACK
  const sample = sampleFromSaved(template.builder.sample)
  return buildSamplePack(team, { ...sample, numbers: templateShowsJerseyNumbers(template) ? sample.numbers : null })
}

// A fourth and fifth leg for a longer sample: a spread on the sample's team and the game's total, in the lines
// and prices that sport bets (a -1.5 run or puck line pays plus money, unlike an American football or basketball spread).
// The total is on the game, not a team, so it draws no jersey or logo, like the Yankees sample's game total.
const SAMPLE_EXTRA_LEGS = {
  american_football: { spread: ['-3.5', 'Spread', '-110'], total: ['Over 47.5', 'Total Points'] },
  basketball: { spread: ['-4.5', 'Spread', '-110'], total: ['Over 224.5', 'Total Points'] },
  baseball: { spread: ['-1.5', 'Run Line', '+135'], total: ['Over 8.5', 'Total Runs'] },
  hockey: { spread: ['-1.5', 'Puck Line', '+165'], total: ['Over 6.5', 'Total Goals'] },
  football: { spread: ['-0.5', 'Spread', '+105'], total: ['Over 2.5', 'Total Goals'] },
  rugby: { spread: ['-6.5', 'Handicap', '-110'], total: ['Over 48.5', 'Total Points'] },
}
// Roughly what the sample's legs pay together at each length (the three-leg price times the extra legs'), so a
// five-leg card does not show a three-leg price. Two legs are the same player props in every sport; the
// spread's price sets the rest.
const SAMPLE_COMBINED_ODDS = {
  american_football: { 2: '+350', 4: '+1250', 5: '+2500' },
  basketball: { 2: '+350', 4: '+1250', 5: '+2500' },
  baseball: { 2: '+350', 4: '+1550', 5: '+3100' },
  hockey: { 2: '+350', 4: '+1750', 5: '+3500' },
  football: { 2: '+350', 4: '+1350', 5: '+2700' },
  rugby: { 2: '+350', 4: '+1250', 5: '+2500' },
}

function sampleLegs(pack, count) {
  if (count <= pack.legs.length) return pack.legs.slice(0, count)
  const teamBet = pack.legs[TEAM_BET_INDEX]
  const { spread, total } = SAMPLE_EXTRA_LEGS[pack.sport] || SAMPLE_EXTRA_LEGS.american_football
  const game = { sport: pack.sport, event_label: teamBet.event_label }
  const extra = [
    { selection_name: `${teamBet.team?.name || teamBet.selection_name} ${spread[0]}`, market_label: spread[1], odds: spread[2], ...game, ...(teamBet.team ? { team: teamBet.team } : {}) },
    { selection_name: total[0], market_label: total[1], odds: '-105', ...game },
  ]
  return [...pack.legs, ...extra].slice(0, count)
}

/**
 * The builder's sample pack (#602): the sample with the preview team
 * (withPreviewTeam) and what the operator set under "Sample content": a
 * title and summary (empty keeps the sample's, which is worded for the leg
 * count), how many legs it shows, the
 * bets placed, and the player legs' shirt numbers (`numbers`: a list, or
 * null while the template shows no numbers). Preview data only; nothing is saved.
 */
export function buildSamplePack(team, { title = '', summary = '', legs = TEMPLATE_SAMPLE_PACK.legs.length, copies, numbers = null } = {}) {
  const dressed = withPreviewTeam(TEMPLATE_SAMPLE_PACK, team, { numbers: numbers || false })
  const count = SAMPLE_LEG_COUNTS.includes(legs) ? legs : dressed.legs.length
  const pack = count === dressed.legs.length ? { ...dressed } : {
    ...dressed,
    legs: sampleLegs(dressed, count),
    combinedOdds: (SAMPLE_COMBINED_ODDS[dressed.sport] || SAMPLE_COMBINED_ODDS.american_football)[count],
    summary: sampleSummary(isSampleEagles(team) ? 'Eagles' : teamNickname(team), count, isSampleEagles(team), dressed.sport),
  }
  const ownTitle = String(title ?? '').trim()
  const ownSummary = String(summary ?? '').trim()
  if (ownTitle) pack.title = ownTitle
  if (ownSummary) pack.summary = ownSummary
  // An empty field keeps the sample's count; anything else is held to what a card can show.
  const placed = copies === '' || copies === null || copies === undefined ? NaN : Math.floor(Number(copies))
  if (Number.isFinite(placed)) pack.copies = Math.min(MAX_SAMPLE_COPIES, Math.max(0, placed))
  return pack
}

function dressSample(pack, team) {
  // What the catalog list adds to a team (nickname, league, sport) is read here and never lands on a leg's team.
  const { logo_url: logoUrl, number: _number, nickname: _nickname, league: _league, sport, ...rest } = team
  const eagles = isSampleEagles(rest)
  const sample = eagles ? pack : sampleForTeam(pack, rest, teamNickname(team), sport)
  return {
    ...sample,
    legs: sample.legs.map((leg, index) => {
      if (!leg.team) return leg
      // The Eagles are merged over the sample's own Eagles; another team replaces them whole, so no Eagles kit is left behind.
      const dressed = eagles ? { ...leg.team, ...rest } : { ...rest }
      return { ...leg, team: logoUrl && index === TEAM_BET_INDEX ? { ...dressed, logo_url: logoUrl } : dressed }
    }),
  }
}

// Team families come first: a tennis "Player Moneyline" is a team_moneyline
// whose participant is a player. Mirrors isPlayerOutcome in the Studio API.
const TEAM_MARKET_FAMILIES = new Set(['team_moneyline', 'team_spread', 'team_total'])

/** Whether a leg backs a player (a prop), so it can carry the player's shirt number (`player_number`). */
export function isPlayerLeg(leg) {
  const family = String(leg?.market_family || leg?.market_type || '').toLowerCase()
  if (TEAM_MARKET_FAMILIES.has(family)) return false
  return String(leg?.participant_type || '').toLowerCase() === 'player' || family.startsWith('player_')
}

/**
 * A leg's team as the server will store it with the operator's choices (#572),
 * for previews only: player_number as team.number, and kit_variant
 * "alternate" as the server's `alternate_kit` (the catalog's documented
 * alternate uniform, else the primary with body and trim swapped), or that
 * swap done here when an older payload has no `alternate_kit`. A team the
 * server already dressed (kit_variant on the team) is left alone. Undefined
 * for a leg without a team.
 */
export function previewLegTeam(leg) {
  if (!leg?.team || typeof leg.team !== 'object') return undefined
  const { number: _number, ...team } = leg.team
  if (leg.player_number) team.number = leg.player_number
  if (leg.kit_variant === 'alternate' && team.kit_variant !== 'alternate') {
    // The server's alternate (the catalog's documented uniform when there is
    // one), so the preview shows what will be saved; older payloads lack it.
    if (team.alternate_kit && typeof team.alternate_kit === 'object') return { ...team, kit: team.alternate_kit, kit_variant: 'alternate' }
    const { number_color: _numberColor, ...kit } = team.kit && typeof team.kit === 'object' ? team.kit : {}
    const colors = Array.isArray(team.colors) ? team.colors : []
    const body = kit.body || colors[0]
    const trim = kit.trim || colors[1]
    // One color has nothing to swap with.
    if (body && trim) return { ...team, kit: { ...kit, body: trim, trim: body }, kit_variant: 'alternate' }
  }
  return team
}

/** Whether a card template draws jerseys: the builder's switch, or a hand-written template using a jersey token (with or without numbers). */
export function templateShowsJerseys(template) {
  return template?.builder?.features?.jerseys === true || JERSEY_TOKEN_PATTERN.test(template?.htmlCode || '')
}

/**
 * Whether a saved template's compiled markup draws shirt numbers: only a bare jersey token
 * (not the plain token) counts, so stale builder options cannot override stored HTML.
 */
export function templateShowsJerseyNumbers(template) {
  if (JERSEY_NUMBERS_RETIRED) return false
  return STYLED_JERSEY_TOKEN_PATTERN.test(template?.htmlCode || '')
}

/** Whether a builder jersey template's compiled HTML is stale and should be re-saved to enable shirt numbers. */
export function templateJerseyNumbersNeedResave(template) {
  return templateShowsJerseys(template)
    && !templateShowsJerseyNumbers(template)
    && Boolean(template?.builder)
    && !template.builder.code
    && template.builder.options?.jerseys?.numbers !== 'hidden'
}

/**
 * Whether the builder's own preview shows shirt numbers: the switches, or, once the HTML is edited by hand (the
 * switches then draw nothing), the token that HTML uses, read the way templateShowsJerseyNumbers reads a saved card.
 * CSS added by hand (#998, builder.css) does not change the markup, so it does not count as edited code here.
 */
export function builderShowsJerseyNumbers(choices) {
  if (JERSEY_NUMBERS_RETIRED) return false
  if (choices?.code) return STYLED_JERSEY_TOKEN_PATTERN.test(choices.code.html || '')
  return choices?.features?.jerseys === true && choices?.options?.jerseys?.numbers !== 'hidden'
}

export function oddsToNum(odds) {
  if (!odds) return 0
  const parsed = Number.parseInt(String(odds).replace('+', ''), 10)
  return Number.isFinite(parsed) ? parsed : 0
}

export function combinedOddsFromLegs(legs = []) {
  if (legs.length === 0) return '—'
  const decimal = legs.reduce((product, leg) => {
    const value = Number(leg.decimal_odds ?? leg.decimalOdds)
    return Number.isFinite(value) && value > 1 ? product * value : product
  }, 1)
  if (decimal <= 1) return '—'
  const american = decimal >= 2 ? Math.round((decimal - 1) * 100) : Math.round(-100 / (decimal - 1))
  return american > 0 ? `+${american}` : String(american)
}

const formatShortDateTime = (value) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(value)

function earliestLegStart(pack) {
  const starts = (pack?.legs || [])
    .map((leg) => Date.parse(leg.event_start_time || leg.eventStartTime || ''))
    .filter(Number.isFinite)
  return starts.length === 0 ? null : Math.min(...starts)
}

export function startTimeLabel(pack) {
  const earliestStart = earliestLegStart(pack)
  if (earliestStart == null) return null
  return formatShortDateTime(earliestStart)
}

export function expiryLabel(pack) {
  const earliestStart = earliestLegStart(pack)
  if (earliestStart == null) return '—'
  if (earliestStart <= Date.now()) return 'Expired'
  const minutes = Math.ceil((earliestStart - Date.now()) / 60000)
  if (minutes < 60) return `${minutes}m`
  if (minutes < 2880) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(earliestStart)
}

/** When a pack last changed, for a list row (#1080): "just now", "5m ago", "3h ago", "2d ago", then the date. */
export function updatedLabel(pack, now = Date.now()) {
  const updated = Date.parse(pack?.updated_at || '')
  if (!Number.isFinite(updated)) return '—'
  const minutes = Math.floor(Math.max(0, now - updated) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`
  if (minutes < 10080) return `${Math.floor(minutes / 1440)}d ago`
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(updated)
}

export function aiScore(pack) {
  const value = Number(pack?.ai_score ?? pack?.aiScore ?? pack?.metadata?.ai_score)
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null
}

export function aiScoreColor(score) {
  if (score == null) return 'var(--wf-color-text-muted)'
  if (score >= 80) return 'var(--wf-color-success)'
  if (score >= 65) return 'var(--wf-color-action-text)'
  if (score >= 50) return 'var(--wf-color-warning)'
  return 'var(--wf-color-text-muted)'
}

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1)

// A stored time as the library writes times ("Oct 14, 2:00 PM", the viewer's clock), or null without a usable one.
function stampLabel(stamp) {
  const date = stamp ? new Date(stamp) : null
  if (!date || Number.isNaN(date.getTime())) return null
  return formatShortDateTime(date)
}

/**
 * When a scheduled pack goes live: its `scheduled_at` (the game's kickoff
 * less its schedule's lead), written like the library's other times. Null
 * for a pack in any other status or without a time.
 */
export function scheduledPublishLabel(pack) {
  return pack?.status === 'scheduled' ? stampLabel(pack.scheduled_at) : null
}

// Why a pack is off the shelf. A published pack the sportsbook stopped
// accepting (a line moved, a leg was suspended or removed) carries
// evaluation.revalidation: player feeds already hide it, and the sweep
// archives it after a grace unless it recovers. The archive keeps the reason.
export function packShelfIssue(pack) {
  const evaluation = pack?.evaluation || {}
  // The key's presence is the marker, whatever it holds: the Go reader
  // (prepacks.ReadRevalidationMarker) and the SQL guard use the same rule.
  const marker = evaluation.revalidation
  if (pack?.status === 'published' && Object.prototype.hasOwnProperty.call(evaluation, 'revalidation')) {
    return { kind: 'hidden', label: 'Hidden', detail: 'Hidden from player feeds', reason: capitalize(marker?.reason || 'rejected by the sportsbook'), since: stampLabel(marker?.failed_at) }
  }
  if (pack?.status === 'archived' && evaluation.archive_reason) {
    return { kind: 'archived', label: 'Archived', detail: 'Taken off the shelf', reason: capitalize(evaluation.archive_reason), since: stampLabel(evaluation.archived_at) }
  }
  return null
}

// Why a run saved a pack for title review, by `title_review.reason`.
const TITLE_REVIEW_REASONS = {
  no_valid_title: 'No title passed the checks',
  // #1110: no AI title passed (most often each one only named the bet) and
  // the schedule has no default title to fall back on.
  no_title: 'No acceptable title: no AI title passed the checks, and the schedule has no default title',
}

/**
 * Why a pack's title needs review (#984): a run found no title for it that is
 * valid and unlike the organization's other titles, so it saved the pack as a
 * draft with `evaluation.title_review = { reason, flagged_at }`. The pack
 * cannot be published until its title changes (by hand or Regenerate title).
 * The key's presence is the flag, as prepacks.ReadTitleReviewMarker reads it.
 * Null for a pack without the flag.
 */
export function titleReviewIssue(pack) {
  const evaluation = pack?.evaluation
  if (!evaluation || !Object.prototype.hasOwnProperty.call(evaluation, 'title_review')) return null
  const marker = evaluation.title_review
  const reason = TITLE_REVIEW_REASONS[marker?.reason] || 'Every title tried repeats another pack’s'
  return { kind: 'title_review', label: 'Title needs review', reason, since: stampLabel(marker?.flagged_at) }
}

// How a title was written, as the library labels it (#984); null for an
// operator's own title.
const TITLE_SOURCE_LABELS = { ai: 'AI title', default: 'Default title', factual: 'Auto title' }

/** The library's label for how a pack's title was written, or null. */
export function titleSourceLabel(pack) {
  return TITLE_SOURCE_LABELS[pack?.title_source] || null
}

// What each title source means, for the Surfaces card's label (#1110).
const TITLE_SOURCE_HINTS = {
  ai: 'Written by AI for this pack',
  default: 'The schedule’s default title: no AI title passed the checks',
  factual: 'Built from the pack’s league, teams and markets',
  operator: 'Set by an operator, or the schedule’s name or custom title',
}

/**
 * How a Surfaces card's title was written (#1110), shown beside its AI score
 * so a fallback or a schedule's name is told apart from an AI title: the
 * library's label, and "Operator title" for a title a person or the schedule
 * set. Null when the pack carries no source.
 */
export function cardTitleSource(pack) {
  const source = pack?.title_source
  const label = titleSourceLabel(pack) || (source === 'operator' ? 'Operator title' : null)
  return label ? { label, hint: TITLE_SOURCE_HINTS[source] } : null
}

/**
 * Why a scheduled pack has not gone live yet (#765). At its time the
 * automations tick claims the pack by stamping
 * `evaluation.scheduled_publish = { attempted_at }` and quotes it with the
 * sportsbook: a pack that publishes drops the marker, a refused one keeps it
 * with the refusal as `error` and stays scheduled. The tick tries it again
 * every 5 minutes (prepacks.ScheduledPublishBackoff) until its first game
 * starts, when the pack is deleted. A retry restamps `attempted_at` and keeps
 * the last `error` until it is answered, so `since` is the last attempt.
 * Null for any other status, without a marker, or while the marker holds
 * only `attempted_at` (an attempt still running, or one cut short).
 */
export function scheduledPublishIssue(pack) {
  if (pack?.status !== 'scheduled') return null
  const marker = pack.evaluation?.scheduled_publish
  const error = marker && typeof marker === 'object' && typeof marker.error === 'string' ? marker.error.trim() : ''
  if (!error) return null
  return { kind: 'refused', label: 'Not published yet', reason: capitalize(error), since: stampLabel(marker.attempted_at) }
}

/**
 * The schedule that generated a pack: the `recipe_id` column newer saves fill,
 * else the stamp generation writes into the pack's template variables (an
 * operator's edit keeps it). Null for a pack built by hand.
 */
export const packRecipeId = (pack) => pack?.recipe_id
  || pack?.template_vars?.recipe_id
  || pack?.templateVarOverrides?.recipe_id
  || null

/**
 * The fewest legs a pack publishes with, as the server decides (PackMinLegs):
 * a schedule's pack keeps its saved type, so any type but Straight needs 2;
 * a hand-built pack takes its type from its legs, so one leg is a Straight.
 */
export function publishMinLegs(pack) {
  if (!packRecipeId(pack)) return 1
  return pack?.pack_type && pack.pack_type !== 'straight' ? 2 : 1
}

export function normalizePrepack(pack) {
  const legs = pack?.legs || []
  const firstLeg = legs[0] || {}
  const hasOwn = (key) => Boolean(pack && Object.prototype.hasOwnProperty.call(pack, key))
  const rawLeague = pack?.league || firstLeg.league || ''
  const rawSport = pack?.sport || firstLeg.sport || ''
  const normalizedSport = packSportId(rawSport, rawLeague)
  const combinedOdds = hasOwn('odds')
    ? (pack.odds ?? '—')
    : (hasOwn('combinedOdds') ? (pack.combinedOdds ?? '—') : '—')
  const templateId = hasOwn('template_id') && pack.template_id !== undefined
    ? (pack.template_id ?? null)
    : (hasOwn('templateId') ? (pack.templateId ?? null) : null)
  const templateVars = hasOwn('template_vars') && pack.template_vars !== undefined
    ? (pack.template_vars ?? {})
    : (hasOwn('templateVarOverrides') ? (pack.templateVarOverrides ?? {}) : {})
  return {
    ...pack,
    legs,
    combinedOdds,
    // "3 games" for a pack across games, never the first leg's game as the pack's.
    event: packEventLabel(legs, pack?.event || firstLeg.event_label || firstLeg.eventLabel || '—'),
    sport: normalizedSport ? prepackSportLabel(normalizedSport) : '—',
    league: rawLeague ? rawLeague.toUpperCase() : '—',
    type: packTypeLabel(pack?.type || pack?.pack_type),
    bet_type: hasOwn('bet_type') ? pack.bet_type : (deriveBetType(legs) || undefined),
    tags: Array.isArray(pack?.tags) ? pack.tags : [],
    templateId,
    templateVarOverrides: templateVars,
  }
}

const legEventLabel = (leg) => String(leg?.event_label ?? leg?.eventLabel ?? '').trim()

/**
 * A leg ready to print in a list, as the cards print it: its participant
 * (else its selection), what it bets (`legBetLabel`, #865: the market and
 * side under a participant, else the market), and its matchup only when the
 * selection alone names no team or player (`legNeedsEvent`: "Over 6.5 ·
 * Total Runs · TB Rays @ PIT Pirates", but "Kansas City Chiefs · Moneyline",
 * "Bruno Fernandes · 1+ Goals").
 */
export function prepackLegLabel(leg = {}) {
  const participant = String(leg?.participant ?? leg?.player ?? '').trim()
  return {
    primary: participant || String(leg?.selection_name ?? leg?.selectionName ?? '').trim(),
    secondary: legBetLabel(leg),
    event: legNeedsEvent(leg) ? legEventLabel(leg) : '',
  }
}

/** "Over 6.5 · Total Runs · TB Rays @ PIT Pirates": a label's non-empty parts. */
export function prepackLegText({ primary, secondary, event }) {
  return [primary, secondary, event].filter(Boolean).join(' · ')
}

/**
 * What a run result says about a pack's legs: the matchup once when every leg
 * is in one game, else how many games (`packEventLabel`, never the first
 * leg's game as the pack's), then the first `limit` legs, each naming its own
 * game when it needs one and the pack spans several, and how many more there are.
 */
export function prepackLegSummary(pack, limit = 3) {
  const legs = Array.isArray(pack?.legs) ? pack.legs : []
  const games = packGames(legs)
  return {
    event: packEventLabel(legs, games.size === 1 ? [...games.values()][0] : ''),
    lines: legs.slice(0, limit).map((leg) => {
      const label = prepackLegLabel(leg)
      return games.size > 1 ? label : { ...label, event: '' }
    }),
    more: Math.max(0, legs.length - limit),
  }
}

// The leg shape the Studio API's title and restyle endpoints read. Composer
// legs arrive in either snake_case (API) or camelCase (legacy drafts).
export function canonicalAiLeg(leg = {}) {
  return {
    outcome_id: leg.outcome_id ?? leg.outcomeId,
    event_id: leg.event_id ?? leg.eventId,
    event_label: leg.event_label ?? leg.eventLabel,
    event_start_time: leg.event_start_time ?? leg.eventStartTime,
    sport: leg.sport,
    league: leg.league,
    market_id: leg.market_id ?? leg.marketId,
    market_label: leg.market_label ?? leg.marketLabel,
    market_name: leg.market_name ?? leg.marketName,
    selection_name: leg.selection_name ?? leg.selectionName ?? leg.player,
    participant: leg.participant ?? leg.player,
    line: leg.line,
    period: leg.period,
    side: leg.side,
    regulation: leg.regulation,
    odds: leg.odds ?? leg.american_odds,
    decimal_odds: leg.decimal_odds ?? leg.decimalOdds,
  }
}

export function toPublicPrepack(pack) {
  const legs = (pack?.legs || []).map((leg) => ({
    ...leg,
    outcome_id: leg.outcome_id ?? leg.outcomeId,
    event_id: leg.event_id ?? leg.eventId,
    event_label: leg.event_label || leg.eventLabel,
    event_start_time: leg.event_start_time || leg.eventStartTime,
    selection_name: leg.selection_name || leg.selectionName || leg.player,
    market_label: leg.market_label || leg.marketLabel || leg.bet,
    odds: leg.odds || leg.american_odds,
    decimal_odds: leg.decimal_odds ?? leg.decimalOdds,
  }))
  const canonicalPack = { ...(pack || {}) }
  const canonicalHasTemplateId = Object.prototype.hasOwnProperty.call(canonicalPack, 'template_id')
    && canonicalPack.template_id !== undefined
  const legacyTemplateId = canonicalPack.templateId
  const canonicalHasTemplateVars = Object.prototype.hasOwnProperty.call(canonicalPack, 'template_vars')
    && canonicalPack.template_vars !== undefined
  const legacyTemplateVars = canonicalPack.templateVarOverrides
  const legacyCombinedOdds = canonicalPack.combinedOdds
  delete canonicalPack.templateId
  delete canonicalPack.templateVarOverrides
  delete canonicalPack.combinedOdds
  const hasOwn = (key) => Object.prototype.hasOwnProperty.call(canonicalPack, key)
  const templateId = canonicalHasTemplateId
    ? (canonicalPack.template_id ?? null)
    : (legacyTemplateId ?? null)
  const templateVars = canonicalHasTemplateVars
    ? (canonicalPack.template_vars ?? {})
    : (legacyTemplateVars ?? {})
  const derived = deriveBetType(legs)
  const betType = hasOwn('bet_type') ? canonicalPack.bet_type : (derived || undefined)
  const odds = hasOwn('odds')
    ? (canonicalPack.odds ?? '—')
    : (legacyCombinedOdds ?? '—')
  return {
    ...canonicalPack,
    id: String(canonicalPack.id ?? ''),
    title: canonicalPack.title || 'Untitled prepack',
    pack_type: canonicalPack.pack_type || canonicalPack.type || 'parlay',
    bet_type: betType,
    tags: Array.isArray(canonicalPack.tags) ? canonicalPack.tags : [],
    odds,
    template_id: templateId,
    template_vars: templateVars,
    legs,
  }
}


export function resolveTemplates(config) {
  return normalizeTemplates(config?.card_templates).filter((template) => !template.isFallback)
}
export function defaultTemplateId(config) {
  return resolveTemplates(config).find((template) => template.isDefault)?.id
}


// What a preview shows where a generated banner picture will be: floodlights
// over a dark bowl, drawn inline (a data: URI with no ";" so the renderer's
// value guard lets it through), so no request is made.
const SAMPLE_BANNER_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 768 432"><defs><linearGradient id="s" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#17213d"/><stop offset=".5" stop-color="#3b4f85"/><stop offset="1" stop-color="#1d6b45"/></linearGradient><radialGradient id="g" cx=".5" cy=".5" r=".5"><stop offset="0" stop-color="#fffbe6"/><stop offset=".3" stop-color="#ffe9a3" stop-opacity=".6"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient></defs><rect width="768" height="432" fill="url(#s)"/><ellipse cx="384" cy="455" rx="600" ry="150" fill="#2e9a5c"/><ellipse cx="384" cy="455" rx="430" ry="100" fill="none" stroke="#f1f6f2" stroke-opacity=".8" stroke-width="4"/><path d="M0 330h768" stroke="#f1f6f2" stroke-opacity=".55" stroke-width="3"/><g fill="url(#g)"><circle cx="150" cy="130" r="130"/><circle cx="620" cy="110" r="140"/><circle cx="400" cy="80" r="100"/></g><g fill="#fffdf2"><rect x="138" y="118" width="24" height="26" rx="3"/><rect x="608" y="98" width="24" height="26" rx="3"/><rect x="388" y="68" width="24" height="26" rx="3"/></g><g fill="#fff" fill-opacity=".5"><circle cx="90" cy="290" r="3"/><circle cx="210" cy="305" r="3"/><circle cx="330" cy="290" r="3"/><circle cx="470" cy="300" r="3"/><circle cx="600" cy="290" r="3"/><circle cx="700" cy="305" r="3"/></g></svg>'
// The drawing cut to each shape a generated picture has (bannerPictureAspect, #979): its middle square, its middle
// 2:1 band and its middle 3:1 band.
const SAMPLE_BANNER_VIEWBOXES = { 1: '168 0 432 432', 2: '0 24 768 384', 3: '0 88 768 256' }
const SAMPLE_BANNERS = Object.fromEntries(Object.entries(SAMPLE_BANNER_VIEWBOXES).map(([aspect, viewBox]) => [
  aspect,
  bannerImageValue(`data:image/svg+xml,${encodeURIComponent(SAMPLE_BANNER_SVG.replace('viewBox="0 0 768 432"', `viewBox="${viewBox}"`))}`),
]))
/** The stand-in as a header picture of a row layout: 3:1. */
export const SAMPLE_BANNER_VALUE = SAMPLE_BANNERS[3]
/** The stand-in in the shape a picture is painted in for the fill, the template's layout and its title (#979). */
export function sampleBannerValue({ fill, layout, title } = {}) {
  return SAMPLE_BANNERS[bannerPictureAspect(fill, layout, title)]
}

/** The normalized picture settings of a builder template with an image banner, else null. */
export function bannerImageOf(template) {
  return template?.builder?.banner === BANNER_IMAGE ? normalizeBannerImage(template.builder.image) : null
}

/**
 * What a framing box paints under and over a picture in `fill`, as the card does (#946): `main` is the solid color
 * under it and `wash` the layer over it (a CSS image, or null). Behind the whole card, the scrim in the banner fade
 * (`headerEnd`); in a band above the header (the title below it or none), nothing, since no title sits on it; under
 * a centered title over the header picture, the even wash in the fade; else the header color's (`main`) wash, solid
 * where the title sits. The builder and the pack editor frame with it.
 */
export function bannerFramingWash({ fill, title, main, headerEnd }) {
  if (fill === BANNER_FILL_CARD) return { main: headerEnd, wash: cardScrim(headerEnd) }
  const { placement, alignment } = bannerTitleOf(title, fill)
  if (placement !== TITLE_PLACEMENT_OVER) return { main, wash: null }
  if (alignment === TITLE_ALIGNMENT_CENTER) return { main: headerEnd, wash: centerWash(headerEnd) }
  return { main, wash: bannerOverlay(main) }
}

/**
 * The picture a schedule's sample pack shows (#606): the schedule's own when its packs share one, the stand-in
 * when each pack is painted (or the preview picked as the example, issue 684), else nothing (the template's
 * picture or its colors, from the template itself). A picture turned off shows the colors, as a run's packs
 * do; the schedule's fill, when it chose one, is stamped as a run stamps it, so the sample is drawn in it
 * (issue 683), and so is the framing of a shared picture, its own or the template's (#978).
 */
export function scheduleSampleBannerVars(template, banner) {
  const mode = effectiveBannerMode(template, banner)
  if (mode && banner?.hidden === true) return { [BANNER_HIDDEN_VAR]: true }
  const fill = mode && BANNER_FILLS.includes(banner?.fill) ? { [BANNER_FILL_VARIABLE]: banner.fill } : {}
  if (mode === BANNER_MODE_DYNAMIC && banner?.mode === BANNER_MODE_DYNAMIC && isBannerImageUrl(banner.exampleUrl)) return { [BANNER_IMAGE_VARIABLE]: bannerImageValue(banner.exampleUrl), ...fill }
  if (mode === BANNER_MODE_DYNAMIC) {
    const builder = template?.builder
    return { [BANNER_IMAGE_VARIABLE]: sampleBannerValue({ fill: scheduleBannerFill(template, banner), layout: builder?.layout, title: builder?.title }), ...fill }
  }
  if (mode === BANNER_MODE_STATIC && banner?.mode === BANNER_MODE_STATIC) {
    const framing = scheduleBannerFramingVars(banner, scheduleBannerFill(template, banner))
    if (isBannerImageUrl(banner.url)) return { [BANNER_IMAGE_VARIABLE]: bannerImageValue(banner.url), ...framing, ...fill }
    return { ...framing, ...fill }
  }
  return fill
}
